import React, { useState, useEffect, useRef } from 'react'
import { useApp } from '../context/AppContext'
import { meterUnits, meterBrands, formatReadingValue, getReadingHelperText } from '../utils/constants'
import { UI, inputClass } from '../utils/styles'
import { ButtonPrimary, ButtonSecondary } from './ui/Button'
import { Modal } from './ui/Modal'
import { Trash, Edit, Camera } from './ui/Icons'
import { DecimalInput } from './ui/DecimalInput'

export function Modals() {
  const {
    meters,
    isOwner,
    selectedMeter,
    lastReading,
    newReading,
    setNewReading,
    showQuickAddMeter,
    setShowQuickAddMeter,
    showRolloverModal,
    setShowRolloverModal,
    showSeasonStartConfirmModal,
    setShowSeasonStartConfirmModal,
    showAddMeter,
    setShowAddMeter,
    showMeterDetails,
    setShowMeterDetails,
    meterDetailsId,
    setMeterDetailsId,
    photoPreviewModal,
    setPhotoPreviewModal,
    showExportModal,
    setShowExportModal,
    handleExportPDF,
    newMeter,
    setNewMeter,
    resetNewMeter,
    editingMeterId,
    setEditingMeterId,
    editingMeter,
    setEditingMeter,
    handleAddMeter,
    handleEditMeter,
    handleDeleteMeter,
    handleDeleteReading,
    editingReadingId,
    setEditingReadingId,
    editingReading,
    setEditingReading,
    handleUpdateReading,
    handleUpdateSeasonStartReading,
    saveReading,
    saveFirstReading,
    handleBulkSetSeasonStartFromPreviousSeason,
    showSeasonPrompt,
    dismissSeasonPrompt,
    handleSeasonPromptCarryOver,
    getMeterReadings,
    getSeasonTotal,
    readingPhotoUrls,
    enteredBy,
    setEnteredBy,
    session,
  } = useApp()

  const [editingSeasonStart, setEditingSeasonStart] = useState(false)
  const [seasonStartValue, setSeasonStartValue] = useState('')
  const [seasonStartDateValue, setSeasonStartDateValue] = useState('')
  const [addingFirstReading, setAddingFirstReading] = useState(false)
  const [firstReadingValue, setFirstReadingValue] = useState('')
  const [firstReadingNotes, setFirstReadingNotes] = useState('')
  const [firstReadingPhotoFile, setFirstReadingPhotoFile] = useState(null)
  const [firstReadingPhotoPreview, setFirstReadingPhotoPreview] = useState(null)
  const [firstReadingShowEditMeta, setFirstReadingShowEditMeta] = useState(false)
  const [firstReadingEnteredBy, setFirstReadingEnteredBy] = useState('')
  const [firstReadingDateTime, setFirstReadingDateTime] = useState('')
  const [similarMatches, setSimilarMatches] = useState([])
  const [editPhotoFile, setEditPhotoFile] = useState(null)
  const [editPhotoPreview, setEditPhotoPreview] = useState(null)
  const firstReadingDateRef = useRef(null)
  const [firstReadingDateWidth, setFirstReadingDateWidth] = useState(null)
  const editReadingDateRef = useRef(null)
  const [editReadingDateWidth, setEditReadingDateWidth] = useState(null)

  // Free replaced/closed photo previews — object URLs otherwise leak memory
  // over a long PWA session.
  useEffect(() => {
    const url = firstReadingPhotoPreview
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [firstReadingPhotoPreview])
  useEffect(() => {
    const url = editPhotoPreview
    return () => { if (url) URL.revokeObjectURL(url) }
  }, [editPhotoPreview])

  // Levenshtein distance for fuzzy name matching
  const levenshtein = (a, b) => {
    const m = a.length, n = b.length
    const dp = Array.from({length: m + 1}, () => Array(n + 1).fill(0))
    for (let i = 0; i <= m; i++) dp[i][0] = i
    for (let j = 0; j <= n; j++) dp[0][j] = j
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = a[i-1] === b[j-1]
          ? dp[i-1][j-1]
          : 1 + Math.min(dp[i-1][j-1], dp[i-1][j], dp[i][j-1])
      }
    }
    return dp[m][n]
  }

  // Debounced similarity check against existing meter names
  useEffect(() => {
    if (!newMeter.name || newMeter.name.trim().length < 2 || (!showAddMeter && !showQuickAddMeter)) {
      setSimilarMatches([])
      return
    }
    const timer = setTimeout(() => {
      const input = newMeter.name.trim().toLowerCase()
      const matches = meters
        .map(m => {
          const existing = m.name.toLowerCase()
          if (input === existing) return { name: m.name, exact: true, score: 1 }
          if (input.includes(existing) || existing.includes(input)) return { name: m.name, exact: false, score: 0.8 }
          const dist = levenshtein(input, existing)
          const maxLen = Math.max(input.length, existing.length)
          const similarity = 1 - (dist / maxLen)
          if (similarity > 0.7) return { name: m.name, exact: false, score: similarity }
          return null
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, 2)
      setSimilarMatches(matches)
    }, 300)
    return () => clearTimeout(timer)
  }, [newMeter.name, meters, showAddMeter, showQuickAddMeter])

  // Measure parent width for datetime inputs (iOS Safari workaround)
  useEffect(() => {
    if (firstReadingShowEditMeta && firstReadingDateRef.current) {
      setFirstReadingDateWidth(firstReadingDateRef.current.offsetWidth)
    }
  }, [firstReadingShowEditMeta])

  useEffect(() => {
    if (editingReadingId && editReadingDateRef.current) {
      setEditReadingDateWidth(editReadingDateRef.current.offsetWidth)
    }
  }, [editingReadingId])

  const SimilarMatchesWarning = () => {
    if (similarMatches.length === 0) return null
    return (
      <div className={`mt-2 ${similarMatches[0].exact ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'} border rounded-xl p-3`}>
        {similarMatches[0].exact ? (
          <p className="text-sm text-red-700">A meter named "<span className="font-medium">{similarMatches[0].name}</span>" already exists.</p>
        ) : (
          similarMatches.map((match, i) => (
            <p key={i} className="text-sm text-amber-700">
              {i === 0 ? 'Similar meter found: ' : 'Also similar: '}
              <span className="font-medium">{match.name}</span>
              {i === 0 ? '. Did you mean to add this?' : ''}
            </p>
          ))
        )}
      </div>
    )
  }

  const userName = session?.user?.user_metadata?.first_name || ''

  const formatFirstReadingDateTime = () => {
    if (!firstReadingDateTime) {
      const time = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      return `Today, ${time}`
    }
    const dt = new Date(firstReadingDateTime)
    const now = new Date()
    const isToday = dt.toDateString() === now.toDateString()
    const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    if (isToday) return `Today, ${time}`
    return `${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
  }

  const detailsMeter = meterDetailsId ? meters.find(m => m.id === meterDetailsId) : null
  const detailsReadings = meterDetailsId ? [...getMeterReadings(meterDetailsId)].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []

  return (
    <>
      {/* Quick Add Meter Modal */}
      <Modal isOpen={showQuickAddMeter} onClose={() => { setShowQuickAddMeter(false); resetNewMeter() }} title="Add New Meter">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Meter Name * <span className="text-xs text-slate-400 font-normal">(Typically use the name of the field)</span></label>
                <input type="text" value={newMeter.name} onChange={(e) => setNewMeter({...newMeter, name: e.target.value})} className={inputClass} placeholder="Field Name" />
                <SimilarMatchesWarning />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Acres *</label>
                <input type="number" value={newMeter.acres} onChange={(e) => setNewMeter({...newMeter, acres: e.target.value})} className={inputClass} placeholder="130" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Units *</label>
                <select value={newMeter.units} onChange={(e) => setNewMeter({...newMeter, units: e.target.value})} className={inputClass}>
                  <option value="">Select Units</option>
                  {meterUnits.map(unit => (<option key={unit} value={unit}>{unit}</option>))}
                </select>
              </div>
              <div className="pt-3">
                <ButtonPrimary onClick={handleAddMeter} className="w-full">Add Meter</ButtonPrimary>
              </div>
            </div>
      </Modal>

      {/* Rollover Modal */}
      <Modal isOpen={showRolloverModal} onClose={() => setShowRolloverModal(false)} title="Meter Rollover Detected">
            <p className="text-sm text-slate-500 leading-relaxed mb-6">This reading is lower than the previous reading.</p>
            <div className="flex gap-3">
              <ButtonPrimary onClick={() => { setShowRolloverModal(false); saveReading() }} className="flex-1">Confirm Rollover</ButtonPrimary>
              <ButtonSecondary onClick={() => setShowRolloverModal(false)} className="flex-1">Edit Reading</ButtonSecondary>
            </div>
      </Modal>

      {/* Season Start Confirm Modal */}
      <Modal isOpen={showSeasonStartConfirmModal} onClose={() => setShowSeasonStartConfirmModal(false)} title="Confirm Season Start">
            <p className="text-sm text-slate-500 leading-relaxed mb-6">This will set the season starting reading for all meters based on their last reading from the previous season.</p>
            <div className="flex gap-3">
              <ButtonPrimary onClick={handleBulkSetSeasonStartFromPreviousSeason} className="flex-1">Confirm</ButtonPrimary>
              <ButtonSecondary onClick={() => setShowSeasonStartConfirmModal(false)} className="flex-1">Cancel</ButtonSecondary>
            </div>
      </Modal>

      {/* Add Meter Modal */}
      <Modal isOpen={showAddMeter} onClose={() => { setShowAddMeter(false); resetNewMeter() }} title="Add New Meter">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Meter Name * <span className="text-xs text-slate-400 font-normal">(Typically use the name of the field)</span></label>
                <input type="text" value={newMeter.name} onChange={(e) => setNewMeter({...newMeter, name: e.target.value})} className={inputClass} />
                <SimilarMatchesWarning />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Acres *</label>
                <input type="number" value={newMeter.acres} onChange={(e) => setNewMeter({...newMeter, acres: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Units *</label>
                <select value={newMeter.units} onChange={(e) => setNewMeter({...newMeter, units: e.target.value})} className={inputClass}>
                  <option value="">Select Units</option>
                  {meterUnits.map(unit => (<option key={unit} value={unit}>{unit}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Brand</label>
                <select value={newMeter.brand} onChange={(e) => setNewMeter({...newMeter, brand: e.target.value})} className={inputClass}>
                  <option value="">Select Brand</option>
                  {meterBrands.map(brand => (<option key={brand} value={brand}>{brand}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Well Output (GPM)</label>
                <input type="number" value={newMeter.wellOutputGpm} onChange={(e) => setNewMeter({...newMeter, wellOutputGpm: e.target.value})} className={inputClass} placeholder="e.g. 500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Serial Number</label>
                <input type="text" value={newMeter.serialNumber} onChange={(e) => setNewMeter({...newMeter, serialNumber: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Notes</label>
                <textarea value={newMeter.notes} onChange={(e) => setNewMeter({...newMeter, notes: e.target.value})} className={inputClass} rows={2} />
              </div>
              <div className="flex items-center gap-2 py-2">
                <input type="checkbox" id="isSeasonStart" checked={newMeter.isSeasonStart} onChange={(e) => setNewMeter({...newMeter, isSeasonStart: e.target.checked})} className="w-4 h-4" />
                <label htmlFor="isSeasonStart" className="text-sm text-slate-500">Set season starting reading</label>
              </div>
              {newMeter.isSeasonStart && (
                <div>
                  <label className="block text-sm font-medium text-slate-500 mb-1.5">Starting Reading *</label>
                  {/* DecimalInput, same as Add Reading — a plain number input here
                      meant the decimal wasn't auto-placed for ×0.0 and FlowCom meters */}
                  <DecimalInput
                    value={newMeter.seasonStartReading}
                    onChange={(e) => setNewMeter({...newMeter, seasonStartReading: e.target.value})}
                    units={newMeter.units}
                    className={inputClass}
                  />
                  {getReadingHelperText(newMeter.units) && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">{getReadingHelperText(newMeter.units)}</div>
                  )}
                </div>
              )}
              <div className="pt-3">
                <ButtonPrimary onClick={handleAddMeter} className="w-full">Add Meter</ButtonPrimary>
              </div>
            </div>
      </Modal>

      {/* Edit Meter Modal */}
      {editingMeter && (
      <Modal isOpen={!!editingMeterId} onClose={() => { setEditingMeterId(null); setEditingMeter(null) }} title="Edit Meter">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Meter Name</label>
                <input type="text" value={editingMeter.name} onChange={(e) => setEditingMeter({...editingMeter, name: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Acres</label>
                <input type="text" inputMode="decimal" value={editingMeter.acres} onChange={(e) => setEditingMeter({...editingMeter, acres: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Units</label>
                <select value={editingMeter.units} onChange={(e) => setEditingMeter({...editingMeter, units: e.target.value})} className={inputClass}>
                  <option value="">Select Units</option>
                  {meterUnits.map(unit => (<option key={unit} value={unit}>{unit}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Brand</label>
                <select value={editingMeter.brand || ''} onChange={(e) => setEditingMeter({...editingMeter, brand: e.target.value})} className={inputClass}>
                  <option value="">Select Brand</option>
                  {meterBrands.map(brand => (<option key={brand} value={brand}>{brand}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Well Output (GPM)</label>
                <input type="number" value={editingMeter.well_output_gpm || ''} onChange={(e) => setEditingMeter({...editingMeter, well_output_gpm: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Serial Number</label>
                <input type="text" value={editingMeter.serial_number || ''} onChange={(e) => setEditingMeter({...editingMeter, serial_number: e.target.value})} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Meter Notes</label>
                <textarea value={editingMeter.notes || ''} onChange={(e) => setEditingMeter({...editingMeter, notes: e.target.value})} className={inputClass} rows={2} />
              </div>
              <div className="pt-3">
                <ButtonPrimary onClick={handleEditMeter} className="w-full">Save Changes</ButtonPrimary>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <ButtonSecondary
                  onClick={async () => { await handleDeleteMeter(editingMeterId); setEditingMeterId(null); setEditingMeter(null) }}
                  className="w-full !text-red-500 !border-red-200 hover:!bg-red-50"
                >
                  Delete This Meter
                </ButtonSecondary>
              </div>
            </div>
      </Modal>
      )}

      {/* Photo Preview Modal */}
      {photoPreviewModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4 animate-fade-in" onClick={() => setPhotoPreviewModal(null)}>
          <div className="relative max-w-4xl max-h-full animate-modal-in">
            <img src={photoPreviewModal} alt="Full size meter photo" className="max-w-full max-h-[90vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
            <button onClick={() => setPhotoPreviewModal(null)} className="absolute top-3 right-3 bg-black/50 text-white rounded-full w-9 h-9 flex items-center justify-center hover:bg-black/70 backdrop-blur-sm transition-colors text-lg">×</button>
          </div>
        </div>
      )}

      {/* Meter Details Modal */}
      {detailsMeter && (
      <Modal isOpen={showMeterDetails} onClose={() => { setShowMeterDetails(false); setAddingFirstReading(false); setFirstReadingValue(''); setFirstReadingNotes(''); setFirstReadingPhotoFile(null); setFirstReadingPhotoPreview(null); setFirstReadingShowEditMeta(false); setFirstReadingEnteredBy(''); setFirstReadingDateTime(''); setEditingSeasonStart(false) }} title={detailsMeter.name} maxWidth="max-w-2xl">
            <p className="text-sm text-slate-400 -mt-2 mb-5">{detailsMeter.acres} acres</p>

            {/* Add First Reading (when no readings exist and no season start) */}
            {isOwner && detailsReadings.length === 0 && (detailsMeter.season_starting_reading === null || detailsMeter.season_starting_reading === undefined) && (
              <div className="mb-3 p-4 bg-slate-50/80 border border-slate-100 rounded-xl">
                {addingFirstReading ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1.5">Current Reading</label>
                      <DecimalInput
                        value={firstReadingValue}
                        onChange={(e) => setFirstReadingValue(e.target.value)}
                        units={detailsMeter.units}
                        className={inputClass}
                        placeholder="Enter current meter reading"
                        autoFocus
                      />
                    </div>
                    {getReadingHelperText(detailsMeter.units) && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                        {getReadingHelperText(detailsMeter.units)}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1.5">Notes (Optional)</label>
                      <input type="text" value={firstReadingNotes} onChange={(e) => setFirstReadingNotes(e.target.value)} className={inputClass} placeholder="Any additional notes..." />
                    </div>
                    <div>
                      {firstReadingPhotoPreview ? (
                        <div className="flex items-center gap-3">
                          <img src={firstReadingPhotoPreview} alt="Reading preview" className="w-12 h-12 object-cover rounded-xl" />
                          <button type="button" onClick={() => { setFirstReadingPhotoFile(null); setFirstReadingPhotoPreview(null) }} className="text-sm text-red-500">Remove</button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-2 cursor-pointer text-sky-700 hover:text-sky-800 transition-colors">
                          <Camera size={20} />
                          <span className="text-sm">Add Photo</span>
                          <input type="file" accept="image/*" capture="environment" onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file && file.type.startsWith('image/')) {
                              setFirstReadingPhotoFile(file)
                              setFirstReadingPhotoPreview(URL.createObjectURL(file))
                            }
                          }} className="hidden" />
                        </label>
                      )}
                    </div>
                    {/* Entered by / Date & Time */}
                    <div>
                      {firstReadingShowEditMeta ? (
                        <div className="space-y-3" ref={firstReadingDateRef}>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Entered By</label>
                            <input type="text" value={firstReadingEnteredBy} onChange={(e) => setFirstReadingEnteredBy(e.target.value)} className={inputClass} placeholder="Your name" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Date & Time</label>
                            <input type="datetime-local" value={firstReadingDateTime} onChange={(e) => setFirstReadingDateTime(e.target.value)} className={inputClass} style={firstReadingDateWidth ? { width: `${firstReadingDateWidth}px` } : undefined} />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-sm text-slate-400">Entered by {firstReadingEnteredBy || userName || 'Unknown'}</span>
                          <span className="text-sm text-slate-300">·</span>
                          <span className="text-sm text-slate-400">{formatFirstReadingDateTime()}</span>
                          <button
                            onClick={() => {
                              if (!firstReadingEnteredBy && userName) setFirstReadingEnteredBy(userName)
                              setFirstReadingShowEditMeta(true)
                            }}
                            className="text-sm text-sky-700 hover:text-sky-800 ml-1"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-sm text-sky-700">
                      This reading will be saved as the season start baseline for <span className="font-medium">{detailsMeter.name}</span>.
                    </div>
                    <ButtonPrimary
                      onClick={async () => {
                        await saveFirstReading(detailsMeter.id, firstReadingValue, firstReadingNotes, firstReadingPhotoFile, {
                          enteredBy: firstReadingEnteredBy,
                          dateTime: firstReadingDateTime || undefined
                        })
                        setAddingFirstReading(false)
                        setFirstReadingValue('')
                        setFirstReadingNotes('')
                        setFirstReadingPhotoFile(null)
                        setFirstReadingPhotoPreview(null)
                        setFirstReadingShowEditMeta(false)
                        setFirstReadingEnteredBy('')
                        setFirstReadingDateTime('')
                      }}
                      className="w-full"
                    >
                      Save Reading
                    </ButtonPrimary>
                    <button
                      onClick={() => { setAddingFirstReading(false); setFirstReadingValue(''); setFirstReadingNotes(''); setFirstReadingPhotoFile(null); setFirstReadingPhotoPreview(null); setFirstReadingShowEditMeta(false); setFirstReadingEnteredBy(''); setFirstReadingDateTime('') }}
                      className="w-full text-sm text-slate-500 hover:text-slate-700 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingFirstReading(true)}
                    className="text-sm font-medium text-sky-700 hover:text-sky-800"
                  >
                    Add First Reading of the Season →
                  </button>
                )}
              </div>
            )}

            {/* Season Total — hide when only season start readings exist */}
            {detailsReadings.some(r => !(parseFloat(r.start) === parseFloat(r.end) && parseFloat(r.inches) === 0)) && (
              <div className="flex justify-between items-center p-4 bg-slate-50/80 border border-slate-100 rounded-xl mb-5">
                <span className="text-sm font-medium text-slate-500">Season Total</span>
                <span className="text-2xl font-bold text-slate-800 tracking-tight">{getSeasonTotal(detailsMeter.id)}"</span>
              </div>
            )}

            {/* Reading History — hide when empty */}
            {detailsReadings.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Reading History ({detailsReadings.length})</h4>
              {detailsReadings.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {detailsReadings.map((reading, idx) => (
                    <div key={reading.id} className="p-3 rounded-xl bg-white border border-slate-100">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          {parseFloat(reading.start) === parseFloat(reading.end) && parseFloat(reading.inches) === 0 ? (
                            <>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">
                                  {formatReadingValue(reading.end, detailsMeter.units)}
                                </span>
                                <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">Season start</span>
                                {idx === 0 && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Latest</span>}
                              </div>
                              <p className="text-sm text-slate-400">
                                Entered by {reading.entered_by || 'Unknown'} · {new Date(reading.created_at).toLocaleDateString()}, {new Date(reading.created_at).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})}
                              </p>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">
                                  {reading.inches ? `${parseFloat(reading.inches).toFixed(2)}"` : '0.00"'}
                                </span>
                                {idx === 0 && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Latest</span>}
                                {parseFloat(reading.end) < parseFloat(reading.start) && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Meter rollover</span>}
                              </div>
                              <p className="text-sm text-slate-500">
                                {new Date(reading.created_at).toLocaleDateString()} at {new Date(reading.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </p>
                              <p className="text-xs text-slate-400">
                                Previous: {formatReadingValue(reading.start, detailsMeter.units)}{detailsReadings[idx + 1] ? ` (${new Date(detailsReadings[idx + 1].created_at).toLocaleDateString()})` : ''} → Current: {formatReadingValue(reading.end, detailsMeter.units)} | By: {reading.entered_by || 'Unknown'}
                              </p>
                            </>
                          )}
                          {reading.notes && <p className="text-sm text-slate-600 mt-1 italic break-words">{reading.notes}</p>}
                          {readingPhotoUrls[reading.id] && (
                            <button onClick={() => setPhotoPreviewModal(readingPhotoUrls[reading.id])} className="mt-2">
                              <img src={readingPhotoUrls[reading.id]} alt="Meter photo" className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isOwner && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingReadingId(reading.id)
                                  setEditingReading({...reading, created_at: new Date(reading.created_at).toISOString().slice(0, 16)})
                                }}
                                className="p-1 text-slate-400 hover:text-sky-600"
                                title="Edit reading"
                              >
                                <Edit />
                              </button>
                              <button
                                onClick={() => handleDeleteReading(reading.id)}
                                className="p-1 text-slate-400 hover:text-red-600"
                                title="Delete reading"
                              >
                                <Trash />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            )}

      </Modal>
      )}
      {/* Edit Reading Modal */}
      {editingReading && (
      <Modal isOpen={!!editingReadingId} onClose={() => { setEditingReadingId(null); setEditingReading(null); setEditPhotoFile(null); setEditPhotoPreview(null) }} title="Edit Reading">
            <div className="space-y-3" ref={editReadingDateRef}>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Reading</label>
                <DecimalInput value={editingReading.end} onChange={(e) => setEditingReading({...editingReading, end: e.target.value})} units={meters.find(m => m.id === editingReading.meter_id)?.units} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Date & Time</label>
                <input type="datetime-local" value={editingReading.created_at} onChange={(e) => setEditingReading({...editingReading, created_at: e.target.value})} className={inputClass} style={editReadingDateWidth ? { width: `${editReadingDateWidth}px` } : undefined} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1.5">Photo</label>
                {readingPhotoUrls[editingReadingId] && !editPhotoPreview && (
                  <img src={readingPhotoUrls[editingReadingId]} alt="Current photo" className="w-20 h-20 object-cover rounded-lg border border-slate-200 mb-2" />
                )}
                {editPhotoPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={editPhotoPreview} alt="New photo" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                    <button type="button" onClick={() => { setEditPhotoFile(null); setEditPhotoPreview(null) }} className="text-sm text-red-500">Remove</button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sky-700 hover:text-sky-800 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    <span className="text-sm">{readingPhotoUrls[editingReadingId] ? 'Replace Photo' : 'Add Photo'}</span>
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file && file.type.startsWith('image/')) {
                        setEditPhotoFile(file)
                        setEditPhotoPreview(URL.createObjectURL(file))
                      }
                    }} className="hidden" />
                  </label>
                )}
              </div>
              <div className="pt-3">
                <ButtonPrimary onClick={() => { handleUpdateReading(editPhotoFile); setEditPhotoFile(null); setEditPhotoPreview(null) }} className="w-full">Save Changes</ButtonPrimary>
              </div>
            </div>
      </Modal>
      )}

      {/* Export PDF Modal */}
      <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} title="Choose Report Type">
            <div className="space-y-3">
              <button
                onClick={() => { setShowExportModal(false); handleExportPDF(true) }}
                className="w-full p-4 text-left border border-slate-200 rounded-xl hover:border-sky-300 hover:bg-sky-50/40 transition-all group"
              >
                <div className="font-semibold text-slate-800 mb-1 group-hover:text-sky-800 transition-colors">With meter photos</div>
                <p className="text-sm text-slate-500">One page per meter with photo attachments</p>
              </button>
              <button
                onClick={() => { setShowExportModal(false); handleExportPDF(false) }}
                className="w-full p-4 text-left border border-slate-200 rounded-xl hover:border-sky-300 hover:bg-sky-50/40 transition-all group"
              >
                <div className="font-semibold text-slate-800 mb-1 group-hover:text-sky-800 transition-colors">Text only</div>
                <p className="text-sm text-slate-500">Compact report with readings only</p>
              </button>
            </div>
      </Modal>

      {/* Season Carryover Bottom Sheet */}
      {showSeasonPrompt && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50 animate-fade-in" onClick={dismissSeasonPrompt} />
          <div className="fixed bottom-0 left-0 right-0 z-50 animate-sheet-up">
            <div className="bg-white rounded-t-2xl shadow-2xl max-w-lg mx-auto px-6 pt-4 pb-8">
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
              <h3 className="font-semibold text-lg text-slate-900 tracking-tight mb-1">New season detected</h3>
              <p className="text-sm text-slate-500 mb-6">Start with last season's final readings?</p>
              <div className="space-y-2">
                <ButtonPrimary onClick={handleSeasonPromptCarryOver} className="w-full">Yes, carry over</ButtonPrimary>
                <ButtonSecondary onClick={dismissSeasonPrompt} className="w-full">No thanks</ButtonSecondary>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
